import React from 'react';
import { Truck, Zap, Globe, Package, ArrowRight, MessageCircle, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header6';
import Footer from './components/Footer6';

const ShippingAndExchange6 = () => {
    const navigate = useNavigate();

    const shippingOptions = [
        { icon: Truck, title: 'Standard Shipping', time: '5-7 Business Days', cost: 'Free on orders over \u20B91,499', description: 'Reliable delivery across India via our trusted logistics partners. Orders under \u20B91,499 are charged a flat \u20B9149 shipping fee.' },
        { icon: Zap, title: 'Express Shipping', time: '2-3 Business Days', cost: '\u20B9299', description: 'Priority dispatch and expedited delivery for when you need your handloom piece sooner. Available for most metro cities.' },
        { icon: Globe, title: 'International Shipping', time: '7-14 Business Days', cost: 'Varies by country', description: 'We ship to select countries worldwide. Shipping costs are calculated at checkout based on destination and package weight.' },
    ];

    const exchangeSteps = [
        { step: 1, title: 'Request', description: 'Initiate an exchange request within 7 days of delivery through your account dashboard or by contacting our support team.' },
        { step: 2, title: 'Approval', description: 'Our team will review your request within 24-48 hours and send you a confirmation email with return instructions.' },
        { step: 3, title: 'Ship Back', description: 'Pack the item securely in its original packaging with all tags intact. Schedule a reverse pickup or drop off at the nearest courier point.' },
        { step: 4, title: 'Receive New', description: 'Once we receive and inspect the returned item, your replacement will be dispatched within 2-3 business days.' },
    ];

    const importantNotes = [
        'Exchange requests must be raised within 7 days of delivery.',
        'Items must be unused, unwashed, and in their original condition with all tags attached.',
        'Original packaging must be intact and included with the return.',
        'Custom-made and made-to-order handloom pieces are not eligible for exchange.',
        'Slight variations in handloom products (colour, texture, pattern) are natural characteristics and not considered defects.',
        'Exchanges are subject to availability of the requested size or colour.',
        'First exchange on any order ships free. Subsequent exchanges incur a \u20B9149 shipping fee.',
        'Refunds for eligible items are processed within 5-7 business days to your original payment method.',
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');`}</style>
            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                <section className="pt-[120px] pb-12" style={{ borderBottom: '1px solid #E8DFD1' }}>
                    <div className="max-w-3xl mx-auto text-center px-5">
                        <p className="text-xs font-semibold tracking-[3px] uppercase mb-4" style={{ color: '#8B4513' }}>Policies</p>
                        <h1 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-4xl lg:text-5xl font-normal text-[#2C1810] mb-4">Shipping & Exchange</h1>
                        <p className="text-[#7C6B5D] text-base" style={{ fontWeight: 300 }}>Everything you need to know about delivery and our exchange policy</p>
                    </div>
                </section>

                <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-12 lg:py-16">
                    {/* Shipping Options */}
                    <div className="mb-16">
                        <h2 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '24px', fontWeight: 600, color: '#2C1810', marginBottom: '24px' }}>Shipping Options</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {shippingOptions.map((opt, i) => (
                                <div key={i} style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8DFD1', borderRadius: '4px', padding: '28px' }}>
                                    <opt.icon size={28} style={{ color: '#8B4513', marginBottom: '16px' }} />
                                    <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '17px', fontWeight: 600, color: '#2C1810', marginBottom: '4px' }}>{opt.title}</h3>
                                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#8B4513', marginBottom: '4px' }}>{opt.time}</p>
                                    <p style={{ fontSize: '12px', color: '#5D7A3A', fontWeight: 600, marginBottom: '12px' }}>{opt.cost}</p>
                                    <p style={{ fontSize: '13px', color: '#7C6B5D', lineHeight: 1.7, fontWeight: 300 }}>{opt.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Exchange Process */}
                    <div className="mb-16">
                        <h2 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '24px', fontWeight: 600, color: '#2C1810', marginBottom: '24px' }}>Exchange Process</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {exchangeSteps.map((s) => (
                                <div key={s.step} style={{ textAlign: 'center' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#8B4513', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontFamily: "'Noto Serif Display', serif", fontSize: '18px', fontWeight: 700 }}>
                                        {s.step}
                                    </div>
                                    <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '16px', fontWeight: 600, color: '#2C1810', marginBottom: '8px' }}>{s.title}</h3>
                                    <p style={{ fontSize: '13px', color: '#7C6B5D', lineHeight: 1.7, fontWeight: 300 }}>{s.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Important Notes */}
                    <div className="mb-16">
                        <h2 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '24px', fontWeight: 600, color: '#2C1810', marginBottom: '24px' }}>Important Notes</h2>
                        <div style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8DFD1', borderRadius: '4px', padding: '28px' }}>
                            <ul className="space-y-3">
                                {importantNotes.map((note, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#8B4513', marginTop: '7px', flexShrink: 0 }} />
                                        <p style={{ fontSize: '14px', color: '#7C6B5D', lineHeight: 1.7, fontWeight: 300 }}>{note}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Help */}
                    <div className="text-center" style={{ backgroundColor: '#3B1A08', borderRadius: '4px', padding: '40px' }}>
                        <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '22px', fontWeight: 400, color: '#F5F0E8', marginBottom: '8px' }}>Need Help?</h3>
                        <p style={{ fontSize: '14px', color: '#D4C5B0', marginBottom: '24px', fontWeight: 300 }}>Our support team is available Mon-Sat, 10AM-7PM IST</p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <button onClick={() => navigate('/template6/contact')} className="inline-flex items-center gap-2" style={{ padding: '12px 28px', backgroundColor: '#8B4513', color: '#FFFFFF', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
                                <Mail size={16} /> Email Us
                            </button>
                            <button className="inline-flex items-center gap-2" style={{ padding: '12px 28px', backgroundColor: 'transparent', color: '#F5F0E8', border: '2px solid #6B3410', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
                                <Phone size={16} /> +91 98765 43210
                            </button>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default ShippingAndExchange6;
